// Coding agent handoff on a ticket: the drafted work order, the super admin
// send approval, and the job timeline.
import { useEffect, useState } from 'react';
import { Check, Code2, ExternalLink, Loader2, Pencil, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { ticketsAPI } from '../api/client';
import { CodingAgentStatus } from './CodingAgentStatus';
import type { CodingJob, TicketDetail } from '../types';


// Refresh while an agent works, and while waiting on GitHub merges or the production release.
const WORKING_STATES = new Set<CodingJob['state']>([
  'approved', 'planning', 'building', 'reviewing', 'fixing', 'pr_ready', 'merged',
]);

function when(value: string) {
  return new Date(value).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

function errorText(err: unknown, fallback: string) {
  return (err as { response?: { data?: { error?: string } } }).response?.data?.error || fallback;
}

export default function CodingJobPanel({
  ticket, onChange,
}: { ticket: TicketDetail; onChange: (detail: TicketDetail) => void }) {
  const job = ticket.coding_job;
  const [busy, setBusy] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const [planMode, setPlanMode] = useState<'edit' | 'decline' | null>(null);
  const [planText, setPlanText] = useState('');

  const working = Boolean(job && WORKING_STATES.has(job.state));
  useEffect(() => {
    if (!working) return;
    const timer = window.setInterval(() => {
      ticketsAPI.get(ticket.id).then(onChange).catch(() => undefined);
    }, 15000);
    return () => window.clearInterval(timer);
  }, [working, ticket.id, onChange]);

  if (!job) return null;

  const budgetNote = [...job.events].reverse().find((event) => event.kind === 'budget_pause')?.body || '';

  const act = async (action: () => Promise<TicketDetail>, fail: string, done?: string) => {
    setBusy(true);
    try {
      onChange(await action());
      if (done) toast.success(done);
      return true;
    } catch (err) {
      toast.error(errorText(err, fail));
      return false;
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-navy/10 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Code2 size={18} className="text-navy" />
        <h2 className="font-semibold text-navy">Coding agent</h2>
        {job.requested_by && job.state !== 'draft' && (
          <span className="text-xs text-gray-400">asked by {job.requested_by}</span>
        )}
      </div>

      <CodingAgentStatus
        state={job.state}
        canDecide={job.can_decide}
        model={job.model}
        agentAvailable={job.agent_available}
      />

      <details open={job.state === 'draft' || job.state === 'awaiting_send'} className="rounded-xl bg-off-white border border-gray-100 p-4">
        <summary className="cursor-pointer text-xs font-semibold text-gray-500 uppercase tracking-wide">Work order</summary>
        <p className="mt-2 text-sm text-gray-800 whitespace-pre-wrap">{job.work_order.replace(/\*\*/g, '')}</p>
      </details>

      {job.state === 'draft' && (
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={busy}
            onClick={() => void act(
              () => ticketsAPI.requestCoding(ticket.id),
              'Could not send the request.',
              'Sent to a super admin for approval.',
            )}
            className="flex items-center gap-1.5 px-4 py-2 bg-navy text-white rounded-lg text-sm font-semibold hover:bg-navy-dark transition disabled:opacity-50"
          >
            {busy && <Loader2 size={15} className="animate-spin" />}
            Send to coding agent
          </button>
          <p className="text-xs text-gray-500">
            Want changes? Tell @agent in staff chat and it will redraft this.
          </p>
        </div>
      )}

      {job.state === 'awaiting_send' && !job.can_decide && (
        <p className="text-sm text-gray-600">Waiting for a super admin to approve sending this.</p>
      )}

      {job.state === 'awaiting_send' && job.can_decide && (
        <div className="space-y-2">
          <p className="text-sm text-gray-700">Send this work order to the coding agent?</p>
          {!declining ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => void act(
                  () => ticketsAPI.decideCoding(ticket.id, true),
                  'Could not approve.',
                  'Approved.',
                )}
                className="flex items-center gap-1.5 px-4 py-2 bg-navy text-white rounded-lg text-sm font-semibold hover:bg-navy-dark transition disabled:opacity-50"
              >
                <Check size={15} />
                Approve
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => setDeclining(true)}
                className="flex items-center gap-1.5 px-4 py-2 border border-red-200 text-red-700 rounded-lg text-sm font-semibold hover:bg-red-50 transition disabled:opacity-50"
              >
                <X size={15} />
                Decline
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <textarea
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                rows={2}
                placeholder="Why not? (optional, shown in staff chat)"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:outline-none"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={async () => {
                    if (await act(
                      () => ticketsAPI.decideCoding(ticket.id, false, reason),
                      'Could not decline.',
                      'Declined.',
                    )) {
                      setDeclining(false);
                      setReason('');
                    }
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition disabled:opacity-50"
                >
                  Decline request
                </button>
                <button
                  type="button"
                  onClick={() => setDeclining(false)}
                  className="px-4 py-2 text-sm text-gray-500 hover:text-navy"
                >
                  Back
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {job.state === 'approved' && (
        <p className="text-sm text-gray-600">Approved and queued for the coding agent.</p>
      )}

      {job.state === 'planning' && (
        <p className="text-xs text-gray-500">No code changes until a super admin approves the plan.</p>
      )}

      {job.agent_url && (
        <a
          href={job.agent_url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-navy hover:underline"
        >
          <ExternalLink size={14} />
          Watch the agent live in Cursor
        </a>
      )}

      {job.plan && (
        <details open={job.state === 'awaiting_plan'} className="rounded-xl border border-gold/40 bg-gold/5 p-4">
          <summary className="cursor-pointer text-xs font-semibold text-navy uppercase tracking-wide">
            Plan · version {job.plan_version}
          </summary>
          <p className="mt-2 text-sm text-gray-800 whitespace-pre-wrap">{job.plan.replace(/\*\*/g, '')}</p>
        </details>
      )}

      {job.state === 'awaiting_plan' && !job.can_decide && (
        <p className="text-sm text-gray-600">Waiting for a super admin to review the plan.</p>
      )}

      {job.state === 'awaiting_plan' && job.can_decide && (
        <div className="space-y-2">
          {planMode == null ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => void act(
                  () => ticketsAPI.decidePlan(ticket.id, 'approve'),
                  'Could not approve the plan.',
                  'Plan approved.',
                )}
                className="flex items-center gap-1.5 px-4 py-2 bg-navy text-white rounded-lg text-sm font-semibold hover:bg-navy-dark transition disabled:opacity-50"
              >
                <Check size={15} />
                Approve plan
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => setPlanMode('edit')}
                className="flex items-center gap-1.5 px-4 py-2 border border-gold text-navy rounded-lg text-sm font-semibold hover:bg-gold/10 transition disabled:opacity-50"
              >
                <Pencil size={15} />
                Edit
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => setPlanMode('decline')}
                className="flex items-center gap-1.5 px-4 py-2 border border-red-200 text-red-700 rounded-lg text-sm font-semibold hover:bg-red-50 transition disabled:opacity-50"
              >
                <X size={15} />
                Decline
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <textarea
                value={planText}
                onChange={(event) => setPlanText(event.target.value)}
                rows={3}
                placeholder={
                  planMode === 'edit'
                    ? 'What should change? Answer its questions here too.'
                    : 'Why not? (optional, shown in staff chat)'
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:outline-none"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={busy || (planMode === 'edit' && !planText.trim())}
                  onClick={async () => {
                    const mode = planMode;
                    if (await act(
                      () => ticketsAPI.decidePlan(ticket.id, mode, planText),
                      mode === 'edit' ? 'Could not send your changes.' : 'Could not decline the plan.',
                      mode === 'edit' ? 'Sent. The agent is revising the plan.' : 'Plan declined.',
                    )) {
                      setPlanMode(null);
                      setPlanText('');
                    }
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold text-white transition disabled:opacity-50 ${
                    planMode === 'edit' ? 'bg-navy hover:bg-navy-dark' : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {planMode === 'edit' ? 'Send changes' : 'Decline plan'}
                </button>
                <button
                  type="button"
                  onClick={() => { setPlanMode(null); setPlanText(''); }}
                  className="px-4 py-2 text-sm text-gray-500 hover:text-navy"
                >
                  Back
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {job.state === 'plan_approved' && (
        <p className="text-sm text-gray-600">Plan approved. The build starts in a moment.</p>
      )}

      {['building', 'fixing', 'awaiting_budget'].includes(job.state) && (
        <div>
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>Budget used{job.budget_round > 1 ? ` (round ${job.budget_round})` : ''}</span>
            <span className="font-semibold text-navy">{job.budget_percent}%</span>
          </div>
          <div className="mt-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
            <div
              className={`h-full rounded-full ${job.state === 'awaiting_budget' ? 'bg-gold' : 'bg-navy'}`}
              style={{ width: `${job.budget_percent}%` }}
            />
          </div>
        </div>
      )}

      {job.state === 'awaiting_budget' && (
        <div className="space-y-2">
          {budgetNote && (
            <p className="text-sm text-gray-800 whitespace-pre-wrap rounded-xl bg-gold/5 border border-gold/30 p-3">{budgetNote}</p>
          )}
          {job.can_decide ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => void act(
                  () => ticketsAPI.decideBudget(ticket.id, true),
                  'Could not continue the job.',
                  'Continuing.',
                )}
                className="flex items-center gap-1.5 px-4 py-2 bg-navy text-white rounded-lg text-sm font-semibold hover:bg-navy-dark transition disabled:opacity-50"
              >
                <Check size={15} />
                Keep going
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => void act(
                  () => ticketsAPI.decideBudget(ticket.id, false),
                  'Could not stop the job.',
                  'Stopped.',
                )}
                className="flex items-center gap-1.5 px-4 py-2 border border-red-200 text-red-700 rounded-lg text-sm font-semibold hover:bg-red-50 transition disabled:opacity-50"
              >
                <X size={15} />
                Stop
              </button>
            </div>
          ) : (
            <p className="text-sm text-gray-600">Waiting for a super admin to approve more budget.</p>
          )}
        </div>
      )}

      {job.pr_url && (
        <a
          href={job.pr_url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition"
        >
          <ExternalLink size={15} />
          Open pull request
        </a>
      )}

      {job.state === 'merged' && (
        <p className="text-sm text-gray-600">
          A draft note for the member is ready under Close ticket. Send it now, or wait until it shows Live.
        </p>
      )}

      {job.state === 'live' && (
        <p className="text-sm text-gray-600">Live for members. Close the ticket to send them the note.</p>
      )}

      {(job.state === 'failed' || job.state === 'plan_declined' || job.state === 'needs_you' || job.state === 'pr_closed') && job.events.length > 0 && (
        <p className="text-sm text-gray-600">{job.events[job.events.length - 1].body}</p>
      )}

      {job.state === 'send_declined' && job.decline_reason && (
        <p className="text-sm text-gray-600">Declined: {job.decline_reason}</p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <details className="text-sm">
          <summary className="cursor-pointer text-xs font-semibold text-gray-500">
            Timeline ({job.events.length})
          </summary>
          <ul className="mt-2 space-y-1">
            {job.events.map((event) => (
              <li key={event.id} className="text-xs text-gray-600">
                <span className="text-gray-400">{when(event.created_at)} · </span>
                {event.body}
              </li>
            ))}
          </ul>
        </details>
        {job.is_open && job.can_decide && job.state !== 'draft' && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void act(
              () => ticketsAPI.cancelCoding(ticket.id),
              'Could not cancel the job.',
              'Job cancelled.',
            )}
            className="text-xs text-gray-400 hover:text-red-600 transition"
          >
            Cancel job
          </button>
        )}
      </div>
    </div>
  );
}
